import { useMemo } from "react";
import { Table } from "../../table";
import { useAutoTableProvider } from "../auto-table-provider";
import { DataTableRowExpansion } from "../auto-table-row-expansion/auto-table-row-expansion";
import { AutoTableRowProvider } from "./auto-table-row-provider/auto-table-row-provider";
import { IProps } from "./auto-table-row.types";

/**
 * AutoTableRow component
 * @param index - Index of the row
 * @param row - Data of the row
 * @param rows - All rows data
 * @param children - Child components
 * @param expansion - Row expansion configuration
 * @param onClick - Click event handler
 * @returns Row element
 */
const AutoTableRow = <T,>({ index, row, rows, children, expansion, onClick, ...otherProps }: IProps<T>) => {
    const { highlightedRowsKeys, getRowKey, getRowLink } = useAutoTableProvider();
    const expandedElement = expansion && (
        <DataTableRowExpansion
            colSpan={rows ? rows.length : 0}
            open={expansion.isRowExpanded(row)}
            content={expansion.content({ record: row, index })}
            collapseProps={expansion.collapseProps}
        />
    );

    const rowLink = useMemo(() => (row !== null ? getRowLink?.(row) : undefined), [getRowLink, row]);

    return (
        <>
            <Table.Tr
                data-highlighted={highlightedRowsKeys?.includes(String(getRowKey(row))) ?? false}
                {...otherProps}
                data-clickable={!!onClick || !!rowLink}
                onClick={(e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
                    if (expansion) {
                        const { isExpandable, isRowExpanded, expandOnClick, expandRow, collapseRow } = expansion;
                        if (isExpandable({ record: row, index }) && expandOnClick) {
                            if (isRowExpanded(row)) {
                                collapseRow(row);
                            } else {
                                expandRow(row);
                            }
                        }
                    }
                    onClick?.(e);
                }}
            >
                <AutoTableRowProvider row={row} rowLink={rowLink}>
                    {children}
                </AutoTableRowProvider>
            </Table.Tr>
            {expandedElement}
        </>
    );
};

export { AutoTableRow };
